import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Key } from './Key';

interface VirtualKeyboardProps {
  onKeyPress: (key: string) => void;
  onBackspace: () => void;
  onEnter: () => void;
  onSpace: () => void;
}

export const VirtualKeyboard: React.FC<VirtualKeyboardProps> = ({
  onKeyPress,
  onBackspace,
  onEnter,
  onSpace
}) => {
  const [isShift, setIsShift] = useState(false);
  const [capsLock, setCapsLock] = useState(false);

  const numberRow = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'];
  const topRow = ['q', 'w', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p'];
  const middleRow = ['a', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l'];
  const bottomRow = ['z', 'x', 'c', 'v', 'b', 'n', 'm'];

  const specialChars = {
    '1': '!', '2': '@', '3': '#', '4': '$', '5': '%',
    '6': '^', '7': '&', '8': '*', '9': '(', '0': ')'
  };

  const handleKeyPress = (key: string) => {
    let finalKey = key;
    
    if (isShift || capsLock) {
      if (numberRow.includes(key)) {
        finalKey = specialChars[key as keyof typeof specialChars] || key;
      } else if (key.match(/[a-z]/)) {
        finalKey = key.toUpperCase();
      }
    }
    
    onKeyPress(finalKey);
    
    // Reset shift after key press (not caps lock)
    if (isShift && !capsLock) {
      setIsShift(false);
    }
  };

  const handleShift = () => {
    setIsShift(!isShift);
  };

  const handleCapsLock = () => {
    setCapsLock(!capsLock);
    setIsShift(false);
  };

  const getKeyDisplay = (key: string) => {
    if (isShift || capsLock) {
      if (numberRow.includes(key)) {
        return specialChars[key as keyof typeof specialChars] || key;
      } else if (key.match(/[a-z]/)) {
        return key.toUpperCase();
      }
    }
    return key;
  };

  return (
    <motion.div
      className="p-4 bg-gray-100 rounded-2xl border border-gray-300 shadow-2xl"
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      {/* Number Row */}
      <div className="grid grid-cols-10 gap-2 mb-3">
        {numberRow.map((key) => (
          <Key
            key={key}
            value={key}
            displayValue={getKeyDisplay(key)}
            onPress={handleKeyPress}
            className="h-10 md:h-12"
          />
        ))}
      </div>

      {/* Top Row */}
      <div className="grid grid-cols-10 gap-2 mb-3">
        {topRow.map((key) => (
          <Key
            key={key}
            value={key}
            displayValue={getKeyDisplay(key)}
            onPress={handleKeyPress}
            className="h-10 md:h-12"
          />
        ))}
      </div>

      {/* Middle Row */}
      <div className="flex gap-2 mb-3">
        <Key
          value="caps"
          displayValue="Caps"
          onPress={handleCapsLock}
          className={`h-10 md:h-12 px-3 ${
            capsLock ? "!bg-gray-700 !text-white" : ""
          }`}
        />
        <div className="grid grid-cols-9 gap-2 flex-1">
          {middleRow.map((key) => (
            <Key
              key={key}
              value={key}
              displayValue={getKeyDisplay(key)}
              onPress={handleKeyPress}
              className="h-10 md:h-12"
            />
          ))}
        </div>
        <Key
          value="backspace"
          displayValue="⌫"
          onPress={onBackspace}
          className="h-10 md:h-12 px-3"
        />
      </div>

      {/* Bottom Row */}
      <div className="flex gap-2 mb-3">
        <Key
          value="shift"
          displayValue="⇧"
          onPress={handleShift}
          className={`h-10 md:h-12 px-4 ${
            isShift ? "!bg-gray-700 !text-white" : ""
          }`}
        />
        <div className="grid grid-cols-7 gap-2 flex-1">
          {bottomRow.map((key) => (
            <Key
              key={key}
              value={key}
              displayValue={getKeyDisplay(key)}
              onPress={handleKeyPress}
              className="h-10 md:h-12"
            />
          ))}
        </div>
        <Key
          value="enter"
          displayValue="↵"
          onPress={onEnter}
          className="h-10 md:h-12 px-4"
        />
      </div>

      {/* Space Row */}
      <div className="flex gap-2">
        <Key
          value="."
          displayValue="."
          onPress={handleKeyPress}
          className="h-10 md:h-12 px-4"
        />
        <Key
          value="space"
          displayValue="Space"
          onPress={onSpace}
          className="h-10 md:h-12 flex-1"
        />
        <Key
          value=","
          displayValue=","
          onPress={handleKeyPress}
          className="h-10 md:h-12 px-4"
        />
      </div>
    </motion.div>
  );
};